import { useCreateWorldMarkerV2 } from 'hooks/marker/v2/useCreateWorldMarkerV2';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { useContext, useEffect, useRef, useState } from 'react';
import { CtxEdit } from 'session/ctxEdit';

interface DraggedImageData {
    url: string;
    width: number;
    height: number;
    xOffset: number;
    yOffset: number;
}

interface onDraggedStartData {
    event: React.DragEvent<HTMLImageElement>;
    imageUrl: string;
}

export const useDragAndDropImage = (type: 'icon' | 'image') => {
    const ctxEdit = useContext(CtxEdit);
    const [draggedImage, setDraggedImage] = useState<DraggedImageData | null>();
    const containerRef = useRef<HTMLDivElement>();
    const createWorldMarker = useCreateWorldMarkerV2();

    useEffect(() => {
        function onDrop(e: DragEvent) {
            if (draggedImage) {
                const dropPoint = { x: e.offsetX, y: e.offsetY };
                const width = draggedImage.width;
                const height = draggedImage.height;
                const leftTop = {
                    x: dropPoint.x - width / 2,
                    y: dropPoint.y - height / 2
                };

                const corners = [
                    leftTop,
                    { x: leftTop.x + width, y: leftTop.y },
                    { x: leftTop.x + width, y: leftTop.y + height },
                    { x: leftTop.x, y: leftTop.y + height }
                ];
                const coordinates = corners
                    .map((corner) => ctxEdit.mapRef?.current.unproject([corner.x, corner.y]))
                    .map((e) => ({ lat: e.lat, lng: e.lng }));
                createWorldMarker.createImage({ type, url: draggedImage.url }, coordinates);
                ctxEdit.setWorldAction(EWorldAction.IMAGE_V2);
                setDraggedImage(null);
            }
        }

        function onDragOver(e: DragEvent) {
            e.preventDefault();
        }

        const container = ctxEdit?.mapRef?.current.getContainer();
        if (container) {
            container.addEventListener('drop', onDrop);
            container.addEventListener('dragover', onDragOver);
            return () => {
                container.removeEventListener('drop', onDrop);
                container.removeEventListener('dragover', onDragOver);
            };
        }
    }, [draggedImage]);

    function onDragStart({ event, imageUrl }: onDraggedStartData) {
        const target = event.target as HTMLImageElement;
        const scrollOffset = containerRef.current.scrollTop;

        const width = target.width;
        const height = target.width * (target.naturalHeight / target.naturalWidth);
        setDraggedImage({
            url: imageUrl,
            width: width,
            height: height,
            xOffset: event.clientX - target.offsetLeft,
            yOffset: event.clientY - target.offsetTop + scrollOffset
        });
    }

    function onDragEnd() {
        setDraggedImage(null);
    }

    return { onDragStart, onDragEnd, containerRef };
};
